import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ActorRefFrom} from 'xstate';

import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {
  PlateFieldValue,
  scratchPlateSchemaId,
  useScratchPlateData,
} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateDesignSummaryUtil';
import {PlateMap} from 'samples/plate-maps/PlateMapConstants';
import SampleSchemaStore from 'samples/SampleSchemaStore';

import {CreationMode} from './DefineScratchPlateSettingsStep';
import {PlateMetadataFieldsAction, ScratchPlateDesignState} from './ScratchPlateDesignModalUtil';
import {ScratchPlateMachine} from './ScratchPlateModalMachine';

export interface PlateDesignTemplateLoading {
  creationMode: CreationMode;
  onChangeCreationMode: (mode: CreationMode) => void;
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string, scratchPlateId: string) => void;
  isTemplateLoading: boolean;
  /** Render alongside the settings step; mounts the query that loads the selected template. */
  templateLoaderElement: React.ReactNode;
}

/**
 * Drives the "Create from template" toggle on {@link DefineScratchPlateSettingsStep}: tracks which
 * template is selected, loads it via {@link useScratchPlateData}, and applies it to `plateActor` as a
 * `loadFromTemplate` event once loaded. Shared by {@link ScratchPlateDesignModal} and
 * {@link CreateScratchPlateRecordsModal} so both modals' settings steps behave identically.
 *
 * `onPlateMetadataFieldsAction`/`onPlateSchemaFieldValuesAction`, if given, are each dispatched a
 * `load` action carrying the loaded template's respective plate-level fields once a template
 * finishes loading, and, when switching back to "Create from scratch", either `persistedPlateState`'s
 * fields (if given) or `load: []` — mirroring how `plateActor` is updated for name/size/schema. Neither
 * field set is tracked on `plateActor` (each modal keeps its own local reducer state for them), so this
 * just dispatches into the caller's reducers rather than applying the fields itself.
 *
 * `persistedPlateState`, if given, is what `plateActor` reverts to (instead of being cleared) when
 * switching back to "Create from scratch" — for the update flow, where a plate design already has
 * persisted name/size/schema/plate-map data to fall back to rather than blank fields.
 */
export default function usePlateDesignTemplateLoading(
  plateActor: ActorRefFrom<typeof ScratchPlateMachine>,
  onPlateMetadataFieldsAction?: (action: PlateMetadataFieldsAction) => void,
  onPlateSchemaFieldValuesAction?: (action: PlateMetadataFieldsAction) => void,
  persistedPlateState?: ScratchPlateDesignState
): PlateDesignTemplateLoading {
  const [creationMode, setCreationMode] = useState(CreationMode.SCRATCH);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [templateScratchPlateId, setTemplateScratchPlateId] = useState<string | null>(null);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);

  const handleTemplateLoaded = useCallback(
    (
      plate: {name: string | null; height: number; width: number; plateSchemaId: string | null},
      plateMap: PlateMap,
      plateMetadataFields: Array<PlateFieldValue>,
      plateSchemaFieldValues: Array<PlateFieldValue>
    ) => {
      const plateSchema =
        plate.plateSchemaId != null ? SampleSchemaStore.getPlateSchema(plate.plateSchemaId) : null;
      plateActor.send({
        type: 'loadFromTemplate',
        name: plate.name,
        plateSize: [plate.height, plate.width],
        plateSchema,
        plateMap,
      });
      // `PlateFieldValue` is shaped to match `PlateMetadataFieldEntry` field-for-field, so it needs no remapping.
      onPlateMetadataFieldsAction?.({type: 'load', fields: plateMetadataFields});
      onPlateSchemaFieldValuesAction?.({type: 'load', fields: plateSchemaFieldValues});
      // Unmount the loader once applied to prevent re-firing on refetch
      setTemplateScratchPlateId(null);
      setIsTemplateLoading(false);
    },
    [plateActor, onPlateMetadataFieldsAction, onPlateSchemaFieldValuesAction]
  );

  const handleTemplateError = useCallback(() => {
    setSelectedTemplateId(null);
    setTemplateScratchPlateId(null);
    setIsTemplateLoading(false);
  }, []);

  const onChangeCreationMode = useCallback(
    (newMode: CreationMode) => {
      setCreationMode(newMode);
      if (newMode === CreationMode.SCRATCH) {
        setSelectedTemplateId(null);
        setTemplateScratchPlateId(null);
        setIsTemplateLoading(false);
        if (persistedPlateState != null) {
          plateActor.send({
            type: 'loadFromTemplate',
            name: persistedPlateState.name,
            plateSize: persistedPlateState.plateSize,
            plateSchema: persistedPlateState.plateSchema,
            plateMap: persistedPlateState.plateMap,
          });
          onPlateMetadataFieldsAction?.({
            type: 'load',
            fields: persistedPlateState.plateMetadataFields ?? [],
          });
          onPlateSchemaFieldValuesAction?.({
            type: 'load',
            fields: persistedPlateState.plateSchemaFieldValues ?? [],
          });
        } else {
          plateActor.send({type: 'updateName', name: null});
          plateActor.send({type: 'updatePlateSize', plateSize: null});
          plateActor.send({type: 'updatePlateSchema', plateSchema: null});
          onPlateMetadataFieldsAction?.({type: 'load', fields: []});
          onPlateSchemaFieldValuesAction?.({type: 'load', fields: []});
        }
      }
    },
    [plateActor, onPlateMetadataFieldsAction, onPlateSchemaFieldValuesAction, persistedPlateState]
  );

  const onSelectTemplate = useCallback((templateId: string, scratchPlateId: string) => {
    setSelectedTemplateId(templateId);
    setTemplateScratchPlateId(scratchPlateId);
    setIsTemplateLoading(true);
  }, []);

  return {
    creationMode,
    onChangeCreationMode,
    selectedTemplateId,
    onSelectTemplate,
    isTemplateLoading,
    templateLoaderElement:
      templateScratchPlateId != null ? (
        <TemplateLoader
          scratchPlateId={templateScratchPlateId}
          onLoaded={handleTemplateLoaded}
          onError={handleTemplateError}
        />
      ) : null,
  };
}

function TemplateLoader({
  scratchPlateId,
  onLoaded,
  onError,
}: {
  scratchPlateId: string;
  onLoaded: (
    plate: {name: string | null; height: number; width: number; plateSchemaId: string | null},
    plateMap: PlateMap,
    plateMetadataFields: Array<PlateFieldValue>,
    plateSchemaFieldValues: Array<PlateFieldValue>
  ) => void;
  onError: () => void;
}): null {
  const {
    scratchPlateQuery,
    plate,
    plateMap,
    plateMetadataFields,
    plateSchemaFieldValues,
    dataFrameIsInaccessible,
    dataFrameQueriesPending,
    dataFrameQueriesError,
    schemaStorePending,
    schemaStoreLoadFailed,
  } = useScratchPlateData(scratchPlateId);
  const hasCalledOnLoaded = useRef(false);
  // `handleTemplateLoaded` re-reads `SampleSchemaStore.getPlateSchema` directly rather than using this
  // hook's memoized `plateSchema`, so it must not fire until the store has actually loaded — otherwise
  // a schema-backed template can apply with a null schema if its plate/data frames resolve first.
  // `!schemaStoreLoadFailed` keeps this mutually exclusive with `isError` below: a schema that loaded
  // but didn't contain the referenced id leaves `schemaStorePending` false while `schemaStoreLoadFailed`
  // is true, and without this check `loaded` would go true on the same render as the terminal failure.
  const loaded =
    scratchPlateQuery.type === QueryResultType.SUCCESS &&
    !dataFrameQueriesPending &&
    !schemaStorePending &&
    !schemaStoreLoadFailed &&
    plate != null &&
    plateMap != null;
  // Treat GraphQL error, inaccessible node (SUCCESS but no ScratchPlateNode), inaccessible data
  // frames, and a schema that never resolved (see `useScratchPlateData`'s `schemaStoreLoadFailed`) as
  // terminal failures so the user can pick a different template.
  const isError =
    scratchPlateQuery.type === QueryResultType.ERROR ||
    dataFrameQueriesError ||
    schemaStoreLoadFailed ||
    (scratchPlateQuery.type === QueryResultType.SUCCESS &&
      (plate == null || (!dataFrameQueriesPending && (dataFrameIsInaccessible || plateMap == null))));

  useEffect(() => {
    if (loaded && plate != null && plateMap != null && !hasCalledOnLoaded.current) {
      hasCalledOnLoaded.current = true;
      onLoaded(
        {
          name: plate.name,
          height: plate.height,
          width: plate.width,
          plateSchemaId: scratchPlateSchemaId(plate),
        },
        plateMap,
        plateMetadataFields ?? [],
        plateSchemaFieldValues ?? []
      );
    }
  }, [loaded, plate, plateMap, plateMetadataFields, plateSchemaFieldValues, onLoaded]);

  useEffect(() => {
    if (isError) {
      onError();
    }
  }, [isError, onError]);

  return null;
}
